import React, { useCallback, useRef } from 'react';
import { Button, DatePicker, Select, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { CalendarClock, CalendarDays, CheckCircle2, CircleDot, Clock, Flag, User } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import AssigneeSelector from '../../../components/elements/KanbanBoard/AsigneeSelection';
import TaskCustomFields from '../../../components/elements/KanbanBoard/TaskCustomFields/TaskCustomFields';
import type { ScheduledAndPriorityUpdate } from '../../../api/KanbanTaskApiClient';
import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask } from '../../../stores/KanbanTaskStores';
import type { Employee } from '../../../models/Employee';
import { getPriorityIcon, getPriorityText, isOverdue, priorityOptions } from '../useTaskHelpers';
import PropertyRow from './PropertyRow';
import { useTaskField } from './useTaskField';
import type { TaskField } from './useTaskField';

dayjs.extend(localizedFormat);

const { Text } = Typography;

/**
 * Candidates for the assignee select and the user-typed custom fields. The page passes
 * `employeesStore.users`; the public board passes `[]`.
 */
export type AssignableUser = Employee;

export type TaskSidebarProps = {
  task: KanbanTask;
  store: TaskStore;
  readOnly: boolean;
  isAnonymous: boolean;
  assignableUsers?: AssignableUser[];
  /** Cloudinary base URL for avatars; the page passes `applicationStore.siteConfig.baseCloudinary`. */
  baseCloudinary: string;
  currentUserId?: string;
  /** Renders the author block; the page passes `getUserComponent(...)` (Task 8). */
  renderUser?: (userId: string) => React.ReactNode;
};

/**
 * The API clears a date only when the field arrives as an explicit `null` (what the old modal sent);
 * `ScheduledAndPriorityUpdate` types the dates as optional strings, hence the cast.
 */
const toApiDate = (value: Dayjs | null | undefined): string | undefined =>
  (value ? value.format('YYYY-MM-DD') : null) as unknown as string | undefined;

const asDayjs = (value: Dayjs | string | null | undefined): Dayjs | null => (value ? dayjs(value) : null);

const sameDay = (a: Dayjs | null, b: Dayjs | null): boolean =>
  (a ? a.valueOf() : null) === (b ? b.valueOf() : null);

/**
 * Picker rows save on pick and close on dismiss. The ref (not state) is what tells the two apart:
 * antd fires `onChange` before `onBlur`, so a pick marks itself synchronously and the blur that
 * follows it must not undo the save that is already in flight.
 */
function usePickerRow<T>(field: TaskField<T>) {
  const picked = useRef(false);
  return {
    pick: (value: T) => {
      picked.current = true;
      void field.commit(value);
    },
    dismiss: () => {
      if (picked.current) {
        picked.current = false;
        return;
      }
      field.cancel();
    },
  };
}

function useDatePickerRow(field: TaskField<Dayjs | null>) {
  const picked = useRef(false);
  return {
    pick: (value: Dayjs | null) => {
      picked.current = true;
      void field.commit(value);
    },
    onOpenChange: (open: boolean) => {
      if (open) {
        picked.current = false;
      } else {
        setTimeout(() => {
          if (picked.current) {
            picked.current = false;
            return;
          }
          field.cancel();
        }, 150);
      }
    },
  };
}

const TaskSidebar: React.FC<TaskSidebarProps> = observer(
  ({ task, store, readOnly, isAnonymous, assignableUsers, baseCloudinary, currentUserId, renderUser }) => {
    const { t } = useTranslation();

    const editable = !readOnly;
    // Decision 4: the public board assigns too (Task 7 PATCH assignee), so no `!isAnonymous` guard.
    // The gate is the candidate list instead: the public board has no assignable-user source yet
    // (`employeesStore.getUsers()` is authenticated-only, so the page passes `[]`), and an enabled
    // select over an empty list can only commit an empty draft — i.e. silently unassign the task.
    // With a public user list in place this becomes writable on its own.
    const canAssign = editable && (assignableUsers?.length ?? 0) > 0;

    const startDate = asDayjs(task.startDate);
    const dueDate = asDayjs(task.dueDate);

    /** Start date, due date and priority travel to the server together. */
    const saveSchedule = useCallback(
      (patch: Partial<{ startDate: Dayjs | null; dueDate: Dayjs | null; priority: number }>) => {
        const current = store.getTask(task.id) ?? task;
        const payload: ScheduledAndPriorityUpdate = {
          startDate: toApiDate(patch.startDate !== undefined ? patch.startDate : asDayjs(current.startDate)),
          dueDate: toApiDate(patch.dueDate !== undefined ? patch.dueDate : asDayjs(current.dueDate)),
          priority: patch.priority !== undefined ? patch.priority : current.priority,
        };
        return store.updateTaskScheduledAndPriority(task.id, payload);
      },
      [store, task],
    );

    const scheduleMessages = {
      successMessage: t('TaskUpdated'),
      errorMessage: t('FailedToUpdateTask'),
    };

    const statusField = useTaskField<string>({
      identity: task.id,
      value: task.statusId,
      save: (statusId) => store.updateTaskStatus(task.id, statusId),
      successMessage: t('TaskStatusUpdated'),
      errorMessage: t('FailedToUpdateStatus'),
    });

    const assigneeField = useTaskField<string | undefined>({
      identity: task.id,
      value: task.assigneeId ? String(task.assigneeId) : undefined,
      save: async (assigneeId) => {
        await store.updateTaskAssignee?.(task.id, assigneeId);
      },
      successMessage: t('TaskAssigneeUpdated'),
      errorMessage: t('FailedToUpdateAssignee'),
    });

    const priorityField = useTaskField<number>({
      identity: task.id,
      value: task.priority,
      save: (priority) => saveSchedule({ priority }),
      ...scheduleMessages,
    });

    const startField = useTaskField<Dayjs | null>({
      identity: task.id,
      value: startDate,
      save: (next) => saveSchedule({ startDate: next }),
      equals: sameDay,
      ...scheduleMessages,
    });

    const dueField = useTaskField<Dayjs | null>({
      identity: task.id,
      value: dueDate,
      save: (next) => saveSchedule({ dueDate: next }),
      equals: sameDay,
      ...scheduleMessages,
    });

    const statusRow = usePickerRow(statusField);
    const priorityRow = usePickerRow(priorityField);
    const startRow = useDatePickerRow(startField);
    const dueRow = useDatePickerRow(dueField);

    const assignToMe = async () => {
      if (!currentUserId || !store.updateTaskAssignee) return;
      try {
        await store.updateTaskAssignee(task.id, currentUserId);
        message.success(t('AssignedToYou'));
      } catch {
        message.error(t('FailedToAssign'));
      }
    };

    const status = store.projectStatuses.find((item) => item.id === task.statusId);
    const showAssignToMe =
      canAssign &&
      !assigneeField.editing &&
      !!currentUserId &&
      String(task.assigneeId ?? '') !== String(currentUserId);

    return (
      <aside className="task-drawer__sidebar">
        <div className="task-drawer__sidebar-title">{t('taskDrawerProperties')}</div>

        <PropertyRow
          label={t('Status')}
          icon={<CircleDot size={14} />}
          readOnly={!editable}
          editing={statusField.editing}
          onStartEdit={statusField.startEdit}
          value={
            <Tag color={status?.isFinalStatus ? 'green' : 'blue'} className="task-prop__tag">
              {status?.name ?? t('Unknown')}
            </Tag>
          }
          editor={
            <Select
              autoFocus
              defaultOpen
              className="task-prop__control"
              value={statusField.draft}
              loading={statusField.saving}
              onBlur={statusRow.dismiss}
              onChange={statusRow.pick}
              options={store.projectStatuses.map((item) => ({
                value: item.id,
                label: item.isFinalStatus ? (
                  <span className="task-prop__status-option">
                    {item.name}
                    <CheckCircle2 size={13} />
                  </span>
                ) : (
                  item.name
                ),
              }))}
            />
          }
        />

        <PropertyRow
          label={t('Assignee')}
          icon={<User size={14} />}
          readOnly
          stacked
          value={
            <div className="task-prop__assignee">
              <AssigneeSelector
                selectedTask={task}
                isEditing={assigneeField.editing}
                setIsEditing={(next) => (next ? assigneeField.startEdit() : assigneeField.cancel())}
                editedAssigneeId={assigneeField.draft}
                setEditedAssigneeId={assigneeField.setDraft}
                handleSave={() => void assigneeField.commit()}
                t={t}
                users={assignableUsers ?? []}
                baseCloudinary={baseCloudinary}
                disabled={!canAssign}
              />
              {showAssignToMe ? (
                <Button
                  type="link"
                  size="small"
                  className="task-prop__assign-self"
                  onClick={() => void assignToMe()}
                >
                  {t('AssignToMe')}
                </Button>
              ) : null}
            </div>
          }
        />

        <PropertyRow
          label={t('Priority')}
          icon={<Flag size={14} />}
          readOnly={!editable}
          editing={priorityField.editing}
          onStartEdit={priorityField.startEdit}
          value={
            <span className="task-prop__priority">
              {getPriorityIcon(task.priority)}
              {getPriorityText(task.priority, t)}
            </span>
          }
          editor={
            <Select
              autoFocus
              defaultOpen
              className="task-prop__control"
              value={priorityField.draft}
              loading={priorityField.saving}
              onBlur={priorityRow.dismiss}
              onChange={priorityRow.pick}
              options={priorityOptions(t)}
            />
          }
        />

        <PropertyRow
          label={t('StartDate')}
          icon={<CalendarDays size={14} />}
          readOnly={!editable}
          editing={startField.editing}
          onStartEdit={startField.startEdit}
          value={startDate ? <span>{startDate.format('LL')}</span> : <Text type="secondary">{t('taskDrawerNoDate')}</Text>}
          editor={
            <DatePicker
              autoFocus
              defaultOpen
              allowClear
              className="task-prop__control"
              value={startField.draft}
              maxDate={dueField.draft ?? undefined}
              onOpenChange={startRow.onOpenChange}
              onChange={(value) => startRow.pick(value ?? null)}
            />
          }
        />

        <PropertyRow
          label={t('DueDate')}
          icon={<CalendarClock size={14} />}
          readOnly={!editable}
          editing={dueField.editing}
          onStartEdit={dueField.startEdit}
          value={
            dueDate ? (
              <span className={isOverdue(dueDate.toISOString()) ? 'task-prop__overdue' : undefined}>
                {dueDate.format('LL')}
              </span>
            ) : (
              <Text type="secondary">{t('taskDrawerNoDate')}</Text>
            )
          }
          editor={
            <DatePicker
              autoFocus
              defaultOpen
              allowClear
              className="task-prop__control"
              value={dueField.draft}
              minDate={startField.draft ?? undefined}
              onOpenChange={dueRow.onOpenChange}
              onChange={(value) => dueRow.pick(value ?? null)}
            />
          }
        />

        {/* Estimates are edited in the Time tab; the sidebar only reports the current one. */}
        <PropertyRow
          label={t('EstimatedTime')}
          icon={<Clock size={14} />}
          value={
            task.estimatedTime ? <span>{task.estimatedTime}</span> : <Text type="secondary">{'—'}</Text>
          }
        />

        <div
          className="task-drawer__sidebar-block"
          style={{ pointerEvents: editable ? 'auto' : 'none' }}
        >
          <TaskCustomFields task={task} store={store} users={assignableUsers ?? []} readOnly={!editable} t={t} />
        </div>

        {task.createdDate ? (
          <PropertyRow label={t('CreatedAt')} value={<span>{dayjs(task.createdDate).format('LLL')}</span>} />
        ) : null}

        {task.createdByUserId && renderUser ? (
          <PropertyRow
            label={t('Author')}
            stacked
            value={<div className="task-prop__author">{renderUser(String(task.createdByUserId))}</div>}
          />
        ) : null}
      </aside>
    );
  },
);

export default TaskSidebar;
